<template>
  <!-- Floating chat bubble -->
  <div class="iwt-chat">
    <!-- Trigger button -->
    <button
      class="iwt-chat__bubble"
      :class="{ 'iwt-chat__bubble--open': isOpen }"
      @click="toggleChat"
      aria-label="Chat with Axon"
    >
      <span class="iwt-chat__bubble-icon">
        <svg v-if="!isOpen" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </span>
      <!-- Unread badge — shows only before first open -->
      <span v-if="!hasOpened" class="iwt-chat__badge" aria-hidden="true"></span>
    </button>

    <!-- Chat window -->
    <Transition name="iwt-slide">
      <div v-if="isOpen" class="iwt-chat__window" role="dialog" aria-label="Axon AI Chat" @wheel.stop @touchmove.stop>

        <!-- Header -->
        <div class="iwt-chat__header">
          <div class="iwt-chat__header-info">
            <span class="iwt-chat__status-dot"></span>
            <div>
              <p class="iwt-chat__header-title">Axon</p>
              <p class="iwt-chat__header-sub">iWebStudio-Tech AI · Online</p>
            </div>
          </div>
          <div class="iwt-chat__header-actions">
            <button class="iwt-chat__restart" @click="restartChat" aria-label="Start new conversation" title="Start over">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 .49-4.5" />
              </svg>
            </button>
            <button class="iwt-chat__close" @click="toggleChat" aria-label="Close chat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div class="iwt-chat__messages" ref="messagesEl">
          <div
            v-for="(msg, i) in messages"
            :key="i"
            class="iwt-chat__msg"
            :class="msg.role === 'user' ? 'iwt-chat__msg--user' : 'iwt-chat__msg--bot'"
          >
            <span v-if="msg.role === 'assistant'" class="iwt-chat__avatar" aria-hidden="true">AX</span>
            <div class="iwt-chat__bubble-text">{{ msg.content }}</div>
          </div>

          <!-- Typing indicator -->
          <div v-if="isTyping" class="iwt-chat__msg iwt-chat__msg--bot">
            <span class="iwt-chat__avatar" aria-hidden="true">AX</span>
            <div class="iwt-chat__typing">
              <span></span><span></span><span></span>
            </div>
          </div>

          <!-- Lead success banner -->
          <div v-if="leadCaptured" class="iwt-chat__lead-banner">
            ✅ One of our team members will be in touch shortly!
          </div>
        </div>

        <!-- Suggestion chips -->
        <div v-if="chips.length && !isTyping" class="iwt-chat__chips">
          <button
            v-for="chip in chips"
            :key="chip"
            class="iwt-chat__chip"
            @click="sendChip(chip)"
          >{{ chip }}</button>
        </div>

        <!-- Input bar -->
        <div class="iwt-chat__input-bar">
          <input
            ref="inputEl"
            v-model="inputText"
            type="text"
            class="iwt-chat__input"
            placeholder="Type your message…"
            maxlength="500"
            :disabled="isTyping || leadCaptured"
            @keydown.enter.prevent="sendMessage"
          />
          <button
            class="iwt-chat__send"
            :disabled="!inputText.trim() || isTyping || leadCaptured"
            @click="sendMessage"
            aria-label="Send message"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>

        <!-- Footer link -->
        <div class="iwt-chat__footer">
          <router-link to="/contact" @click="isOpen = false">📋 Open contact form</router-link>
        </div>

      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, nextTick } from 'vue'

// Dev: call PHP server directly (CORS headers allow localhost:8080)
// Prod: same-origin relative path
const CHATBOT_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:3001/chatbot.php'
  : '/chatbot.php'

// ── State ─────────────────────────────────────────────────────────
const isOpen      = ref(false)
const hasOpened   = ref(false)
const inputText   = ref('')
const isTyping    = ref(false)
const leadCaptured = ref(false)
const messages    = ref([])   // { role: 'user'|'assistant', content: string }
const chips       = ref([])
const messagesEl  = ref(null)
const inputEl     = ref(null)

// ── Greeting on first open ────────────────────────────────────────
const GREETING = "Hi there! 👋 I'm Axon, iWebStudio-Tech's AI assistant. I'm here to help you find the right service for your needs. What are you looking for today?"
const INITIAL_CHIPS = ['Web Development', 'Cybersecurity', 'AI Solutions', 'Custom AI']

function restartChat () {
  messages.value    = [{ role: 'assistant', content: GREETING }]
  chips.value       = INITIAL_CHIPS
  leadCaptured.value = false
  inputText.value   = ''
  nextTick(() => {
    scrollToBottom()
    inputEl.value?.focus()
  })
}

function toggleChat () {
  isOpen.value = !isOpen.value

  // Lock body scroll on mobile when chat is open
  if (window.innerWidth <= 575) {
    document.body.style.overflow = isOpen.value ? 'hidden' : ''
  }

  if (isOpen.value && !hasOpened.value) {
    hasOpened.value = true
    messages.value.push({ role: 'assistant', content: GREETING })
    chips.value = INITIAL_CHIPS
    nextTick(() => scrollToBottom())
  }
  if (isOpen.value) {
    nextTick(() => inputEl.value?.focus())
  }
}

// ── Send helpers ──────────────────────────────────────────────────
async function sendChip (text) {
  chips.value = []
  await sendUserMessage(text)
}

async function sendMessage () {
  const text = inputText.value.trim()
  if (!text || isTyping.value || leadCaptured.value) return
  inputText.value = ''
  chips.value = []
  await sendUserMessage(text)
}

async function sendUserMessage (text) {
  messages.value.push({ role: 'user', content: text })
  await nextTick()
  scrollToBottom()

  isTyping.value = true
  await nextTick()
  scrollToBottom()

  try {
    // Build history to send (exclude the greeting and the message we just added)
    const history = messages.value
      .slice(1, -1)   // skip greeting (index 0) and the just-added user message (last)
      .map(m => ({ role: m.role, content: m.content }))

    const res = await fetch(CHATBOT_URL, {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ message: text, history }),
    })

    const data = await res.json()

    if (!res.ok) {
      throw new Error(data.error || `HTTP ${res.status}`)
    }

    messages.value.push({ role: 'assistant', content: data.reply })

    if (data.chips?.length) {
      chips.value = data.chips
    }

    if (data.lead_captured) {
      leadCaptured.value = true
      chips.value = []
    }

  } catch (err) {
    console.error('Chatbot error:', err)
    messages.value.push({
      role:    'assistant',
      content: "Sorry, I'm having trouble connecting right now. Please use the contact form below or try again in a moment.",
    })
    chips.value = []
  } finally {
    isTyping.value = false
    await nextTick()
    scrollToBottom()
    if (!leadCaptured.value) inputEl.value?.focus()
  }
}

// ── Scroll helpers ────────────────────────────────────────────────
function scrollToBottom () {
  if (messagesEl.value) {
    messagesEl.value.scrollTop = messagesEl.value.scrollHeight
  }
}

</script>

<style lang="scss" scoped>
// ── Variables (match brand) ───────────────────────────────────────
$bg-dark:     #070d1a;
$bg-card:     #0d1426;
$bg-input:    #111c30;
$accent:      #00c8ff;
$accent-dim:  rgba(0, 200, 255, 0.12);
$accent-glow: rgba(0, 200, 255, 0.35);
$text:        rgba(255, 255, 255, 0.82);
$text-dim:    rgba(255, 255, 255, 0.45);
$border:      rgba(0, 200, 255, 0.14);
$radius:      14px;
$font:        'Montserrat', sans-serif;

// ── Wrapper ───────────────────────────────────────────────────────
.iwt-chat {
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 9999;
  font-family: $font;

  @media (max-width: 575px) {
    bottom: 20px;
    right: 20px;
  }
}

// ── Floating bubble button ────────────────────────────────────────
.iwt-chat__bubble {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: $accent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 0 $accent-glow;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  position: relative;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 0 20px $accent-glow;
  }

  &--open {
    background: $bg-card;
    border: 1.5px solid $border;
  }

  // Hide bubble on mobile when chat is open — close button in header handles dismiss
  @media (max-width: 575px) {
    &--open {
      opacity: 0;
      pointer-events: none;
      transform: scale(0.7);
    }
  }
}

.iwt-chat__bubble-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 22px;
    height: 22px;
    color: #fff;
  }
}

// Unread badge
.iwt-chat__badge {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ff4d4d;
  border: 2px solid $bg-dark;
  animation: pulse-badge 1.8s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%, 100% { transform: scale(1); opacity: 1; }
  50%       { transform: scale(1.3); opacity: 0.7; }
}

// ── Chat window ───────────────────────────────────────────────────
.iwt-chat__window {
  position: absolute;
  bottom: 68px;
  right: 0;
  width: 360px;
  height: 500px;
  background: $bg-card;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6), 0 0 40px $accent-dim;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 575px) {
    // Full-screen overlay on mobile — like a native chat app
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100dvh; // dynamic viewport height — shrinks with keyboard
    max-height: 100dvh;
    border-radius: 0;
    box-shadow: none;
    bottom: auto;
    right: auto;
  }
}

// ── Slide transition ──────────────────────────────────────────────
.iwt-slide-enter-active,
.iwt-slide-leave-active {
  transition: opacity 0.22s ease, transform 0.25s cubic-bezier(0.34, 1.3, 0.64, 1);
}
.iwt-slide-enter-from,
.iwt-slide-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.97);
}

// On mobile — slide up from bottom like a native sheet
@media (max-width: 575px) {
  .iwt-slide-enter-active,
  .iwt-slide-leave-active {
    transition: opacity 0.25s ease, transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
  }
  .iwt-slide-enter-from,
  .iwt-slide-leave-to {
    opacity: 1;
    transform: translateY(100%);
  }
}

// ── Header ────────────────────────────────────────────────────────
.iwt-chat__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: rgba(0, 200, 255, 0.06);
  border-bottom: 1px solid $border;
  flex-shrink: 0;

  @media (max-width: 575px) {
    padding: 16px 20px;
    padding-top: max(16px, env(safe-area-inset-top));
  }
}

.iwt-chat__header-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.iwt-chat__status-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #00e676;
  box-shadow: 0 0 6px #00e676;
  flex-shrink: 0;
}

.iwt-chat__header-title {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 575px) { font-size: 15px; }
}

.iwt-chat__header-sub {
  font-size: 10px;
  color: $text-dim;
  margin: 0;
  line-height: 1.4;

  @media (max-width: 575px) { font-size: 11px; }
}

.iwt-chat__header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.iwt-chat__restart,
.iwt-chat__close {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  color: $text-dim;
  transition: color 0.2s;
  &:hover { color: $accent; }
  svg { width: 15px; height: 15px; }

  @media (max-width: 575px) {
    padding: 8px;
    svg { width: 20px; height: 20px; }
  }
}

// ── Messages area ─────────────────────────────────────────────────
.iwt-chat__messages {
  flex: 1;
  min-height: 0; // critical for flex overflow scroll
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scroll-behavior: smooth;

  // Scrollbar
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: $border; border-radius: 4px; }

  @media (max-width: 575px) {
    padding: 16px 14px;
    gap: 12px;
  }
}

// ── Message bubble ────────────────────────────────────────────────
.iwt-chat__msg {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 88%;

  &--bot  { align-self: flex-start; }
  &--user { align-self: flex-end; flex-direction: row-reverse; }
}

.iwt-chat__avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: $accent-dim;
  border: 1px solid $border;
  font-size: 8px;
  font-weight: 700;
  color: $accent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.iwt-chat__bubble-text {
  padding: 10px 13px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-wrap: break-word;

  @media (max-width: 575px) {
    font-size: 14px;
    padding: 11px 14px;
  }

  .iwt-chat__msg--bot & {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid $border;
    color: $text;
    border-bottom-left-radius: 4px;
  }

  .iwt-chat__msg--user & {
    background: $accent;
    color: #000;
    font-weight: 600;
    border-bottom-right-radius: 4px;
  }
}

// ── Typing dots ───────────────────────────────────────────────────
.iwt-chat__typing {
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid $border;
  border-radius: 14px;
  border-bottom-left-radius: 4px;
  display: flex;
  gap: 5px;
  align-items: center;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $accent;
    animation: typing-dot 1.2s ease-in-out infinite;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

@keyframes typing-dot {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30%           { transform: translateY(-5px); opacity: 1; }
}

// ── Lead success banner ───────────────────────────────────────────
.iwt-chat__lead-banner {
  background: rgba(0, 230, 118, 0.1);
  border: 1px solid rgba(0, 230, 118, 0.3);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 12px;
  color: #00e676;
  text-align: center;
  font-weight: 600;
  margin-top: 4px;
}

// ── Suggestion chips ──────────────────────────────────────────────
.iwt-chat__chips {
  padding: 8px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border-top: 1px solid $border;
  background: rgba(0,0,0,0.15);
  flex-shrink: 0;

  @media (max-width: 575px) {
    padding: 10px 14px;
    gap: 8px;
  }
}

.iwt-chat__chip {
  background: $accent-dim;
  border: 1px solid rgba(0, 200, 255, 0.3);
  color: $accent;
  font-size: 11px;
  font-weight: 600;
  font-family: $font;
  padding: 5px 11px;
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.18s, color 0.18s, transform 0.15s;
  white-space: nowrap;

  &:hover {
    background: $accent;
    color: #000;
    transform: translateY(-1px);
  }

  @media (max-width: 575px) {
    font-size: 13px;
    padding: 8px 16px;
    min-height: 36px;
    display: flex;
    align-items: center;
  }
}

// ── Input bar ─────────────────────────────────────────────────────
.iwt-chat__input-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid $border;
  background: $bg-input;
  flex-shrink: 0;

  @media (max-width: 575px) {
    padding: 10px 14px;
    gap: 10px;
  }
}

.iwt-chat__input {
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid $border;
  border-radius: 22px;
  padding: 9px 14px;
  font-size: 13px;
  font-family: $font;
  color: $text;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder { color: $text-dim; }
  &:focus { border-color: rgba(0, 200, 255, 0.5); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }

  @media (max-width: 575px) {
    font-size: 16px; // prevents iOS auto-zoom on focus
    padding: 11px 16px;
  }
}

.iwt-chat__send {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: $accent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.18s, opacity 0.18s;

  svg { width: 16px; height: 16px; color: #000; }

  &:hover:not(:disabled) { transform: scale(1.1); }
  &:disabled { opacity: 0.35; cursor: not-allowed; }

  @media (max-width: 575px) {
    width: 44px;
    height: 44px;
    svg { width: 18px; height: 18px; }
  }
}

// ── Footer ────────────────────────────────────────────────────────
.iwt-chat__footer {
  text-align: center;
  padding: 6px 12px 10px;
  font-size: 11px;
  background: $bg-input;
  border-top: 1px solid $border;
  flex-shrink: 0;

  // Safe area for iPhone home bar
  padding-bottom: max(10px, env(safe-area-inset-bottom));

  a {
    color: $text-dim;
    text-decoration: none;
    transition: color 0.2s;
    &:hover { color: $accent; }
  }

  @media (max-width: 575px) {
    font-size: 12px;
    padding: 8px 14px max(12px, env(safe-area-inset-bottom));
  }
}
</style>
